/**
 * Production Prep Queue Page
 *
 * Production manager reviews APPROVED orders before sending to production.
 * - View all APPROVED orders with parsed hat inventory
 * - Build assignments: map mockups to specific hats
 * - Split line item quantities across multiple assignments
 * - Add manual override hats
 * - Download digitized files with proper naming (for USB transfer)
 * - Send orders to IN_PRODUCTION stage
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  Thumbnail,
  Button,
  BlockStack,
  InlineStack,
  Banner,
  Spinner,
  Divider,
  Box,
  Select,
  TextField,
  ProgressBar,
  Icon,
  Modal,
} from '@shopify/polaris';
import { RefreshIcon, ExportIcon, PlusIcon, DeleteIcon, CheckIcon } from '@shopify/polaris-icons';
import {
  getProductionPrepOrders,
  saveAssignments,
  sendOrderToProduction,
  buildDownloadUrl,
  type PrepOrder,
  type AssignmentDraft,
  type HatAssignmentDraft,
  type ParsedHat,
} from '../api/productionPrep';

// ============================================
// Assignment Card Component
// ============================================

function AssignmentCard({
  assignment,
  index,
  order,
  onUpdate,
  onDelete,
  availableHats,
  onImageClick,
}: {
  assignment: AssignmentDraft;
  index: number;
  order: PrepOrder;
  onUpdate: (updated: AssignmentDraft) => void;
  onDelete: () => void;
  availableHats: ParsedHat[];
  onImageClick: (url: string, alt: string) => void;
}) {
  const [showAddHats, setShowAddHats] = useState(false);
  const [showManualHat, setShowManualHat] = useState(false);
  const [manualColor, setManualColor] = useState('');
  const [manualSize, setManualSize] = useState('');
  const [manualQty, setManualQty] = useState('1');

  const mockups = order.files.filter((f) => f.type === 'proof' || f.type === 'mockup');
  const digitizationFiles = order.files.filter((f) => f.type === 'digitization_result');
  const isCompleted = assignment.status === 'completed';

  const mockupFile = order.files.find((f) => f.id === assignment.mockupFileId);

  const mockupOptions = [
    { label: 'Select mockup...', value: '' },
    ...mockups.map((f) => ({ label: f.originalFilename, value: f.id })),
  ];

  const digitizationOptions = [
    { label: 'None', value: '' },
    ...digitizationFiles.map((f) => ({ label: f.originalFilename, value: f.id })),
  ];

  const updateHatQty = (hatIdx: number, delta: number) => {
    const newItems = [...assignment.hatItems];
    const newQty = Math.max(0, newItems[hatIdx].quantity + delta);
    if (newQty === 0) {
      newItems.splice(hatIdx, 1);
    } else {
      newItems[hatIdx] = { ...newItems[hatIdx], quantity: newQty };
    }
    onUpdate({ ...assignment, hatItems: newItems });
  };

  const removeHat = (hatIdx: number) => {
    const newItems = assignment.hatItems.filter((_, i) => i !== hatIdx);
    onUpdate({ ...assignment, hatItems: newItems });
  };

  const addHatsFromOrder = (hat: ParsedHat, qty: number) => {
    const newItem: HatAssignmentDraft = {
      sourceLineItemId: hat.shopifyLineItemId,
      color: hat.color,
      size: hat.size,
      quantity: qty,
    };
    onUpdate({ ...assignment, hatItems: [...assignment.hatItems, newItem] });
    setShowAddHats(false);
  };

  const addManualHat = () => {
    if (!manualColor.trim()) return;
    const newItem: HatAssignmentDraft = {
      color: manualColor.trim(),
      size: manualSize.trim(),
      quantity: parseInt(manualQty) || 1,
      isManualOverride: true,
    };
    onUpdate({ ...assignment, hatItems: [...assignment.hatItems, newItem] });
    setManualColor('');
    setManualSize('');
    setManualQty('1');
    setShowManualHat(false);
  };

  const totalHats = assignment.hatItems.reduce((sum, h) => sum + h.quantity, 0);

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text variant="headingMd" as="h3">
              {(() => {
                const titles = new Set<string>();
                for (const hat of assignment.hatItems) {
                  if (hat.sourceLineItemId) {
                    const ph = order.parsedHats.find((p) => p.shopifyLineItemId === hat.sourceLineItemId);
                    if (ph) titles.add(ph.productTitle);
                  }
                }
                const productTitle = [...titles].join(', ');
                return productTitle
                  ? `${productTitle}${assignment.label ? ` - ${assignment.label}` : ''}`
                  : (assignment.label || `Assignment ${index + 1}`);
              })()}
            </Text>
            {isCompleted && (
              <Badge tone="success">Completed</Badge>
            )}
            <Badge tone="info">{`${totalHats} hat${totalHats !== 1 ? 's' : ''}`}</Badge>
          </InlineStack>
          {!isCompleted && (
            <Button
              tone="critical"
              variant="plain"
              icon={DeleteIcon}
              onClick={onDelete}
            >
              Remove
            </Button>
          )}
        </InlineStack>

        {/* Logo + Mockup Thumbnails + Selection */}
        {(() => {
          const logoFileId = order.logoMappings?.[assignment.mockupFileId];
          const logoFile = logoFileId ? order.files.find((f) => f.id === logoFileId) : null;
          return (
            <InlineStack gap="400" blockAlign="start">
              {logoFile?.downloadUrl && (
                <BlockStack gap="100" inlineAlign="center">
                  <Text as="span" variant="bodySm" tone="subdued">Logo</Text>
                  <div
                    onClick={() => onImageClick(logoFile.downloadUrl!, logoFile.originalFilename)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Thumbnail
                      source={logoFile.downloadUrl}
                      alt={logoFile.originalFilename}
                      size="large"
                    />
                  </div>
                </BlockStack>
              )}
              {logoFile?.downloadUrl && mockupFile?.downloadUrl && (
                <div style={{ display: 'flex', alignItems: 'center', fontSize: '20px', color: '#8c9196' }}>→</div>
              )}
              {mockupFile?.downloadUrl && (
                <BlockStack gap="100" inlineAlign="center">
                  <Text as="span" variant="bodySm" tone="subdued">Mockup</Text>
                  <div
                    onClick={() => onImageClick(mockupFile.downloadUrl!, mockupFile.originalFilename)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Thumbnail
                      source={mockupFile.downloadUrl}
                      alt={mockupFile.originalFilename}
                      size="large"
                    />
                  </div>
                </BlockStack>
              )}
            </InlineStack>
          );
        })()}
        <BlockStack gap="300">
          <Select
            label="Mockup"
            options={mockupOptions}
            value={assignment.mockupFileId}
            onChange={(val) => onUpdate({ ...assignment, mockupFileId: val })}
            disabled={isCompleted}
          />
          {digitizationFiles.length > 0 && (
            <Select
              label="Digitization File"
              options={digitizationOptions}
              value={assignment.digitizationFileId || ''}
              onChange={(val) =>
                onUpdate({ ...assignment, digitizationFileId: val || undefined })
              }
              disabled={isCompleted}
            />
          )}
        </BlockStack>

        {/* Label */}
        {!isCompleted && (
          <TextField
            label="Label (optional)"
            value={assignment.label || ''}
            onChange={(val) => onUpdate({ ...assignment, label: val || undefined })}
            placeholder="e.g. Front Logo, Back Patch"
            autoComplete="off"
          />
        )}

        {/* Assignment Product Type (shown when order has both embroidery and patch) */}
        {order.productType === 'both' && !isCompleted && (
          <Select
            label="Embellishment Type"
            options={[
              { label: 'Select type...', value: '' },
              { label: 'Embroidery', value: 'embroidery' },
              { label: 'Patch', value: 'patch' },
            ]}
            value={assignment.productType || ''}
            onChange={(val) => onUpdate({ ...assignment, productType: (val as 'embroidery' | 'patch') || undefined })}
          />
        )}
        {order.productType === 'both' && isCompleted && assignment.productType && (
          <InlineStack gap="200" blockAlign="center">
            <Text variant="bodySm" tone="subdued" as="span">Type:</Text>
            <Badge tone={assignment.productType === 'embroidery' ? 'success' : 'warning'}>
              {assignment.productType}
            </Badge>
          </InlineStack>
        )}

        <Divider />

        {/* Hat Items Table */}
        <Text variant="headingSm" as="h4">Hats in this assignment</Text>
        {assignment.hatItems.length === 0 ? (
          <Text tone="subdued" as="p">No hats assigned yet</Text>
        ) : (
          <BlockStack gap="200">
            {assignment.hatItems.map((hat, hatIdx) => (
              <Box
                key={hatIdx}
                padding="200"
                background="bg-surface-secondary"
                borderRadius="200"
              >
                <InlineStack align="space-between" blockAlign="center">
                  <InlineStack gap="200" blockAlign="center">
                    <Text variant="bodyMd" fontWeight="semibold" as="span">
                      {(() => {
                        const ph = hat.sourceLineItemId
                          ? order.parsedHats.find((p) => p.shopifyLineItemId === hat.sourceLineItemId)
                          : null;
                        const title = ph?.productTitle;
                        const color = hat.color || 'Unknown';
                        return title ? `${title} - ${color}` : color;
                      })()}
                    </Text>
                    {hat.size && (
                      <Badge>{hat.size}</Badge>
                    )}
                    {hat.isManualOverride && (
                      <Badge tone="attention">Manual</Badge>
                    )}
                  </InlineStack>
                  <InlineStack gap="200" blockAlign="center">
                    {!isCompleted && (
                      <Button
                        size="slim"
                        onClick={() => updateHatQty(hatIdx, -1)}
                      >
                        −
                      </Button>
                    )}
                    <Text variant="headingSm" as="span">
                      {hat.quantity}
                    </Text>
                    {!isCompleted && (
                      <>
                        <Button
                          size="slim"
                          onClick={() => updateHatQty(hatIdx, 1)}
                        >
                          +
                        </Button>
                        <Button
                          size="slim"
                          tone="critical"
                          variant="plain"
                          onClick={() => removeHat(hatIdx)}
                        >
                          Remove
                        </Button>
                      </>
                    )}
                  </InlineStack>
                </InlineStack>
              </Box>
            ))}
          </BlockStack>
        )}

        {/* Add Hats Actions */}
        {!isCompleted && (
          <InlineStack gap="200">
            <Button
              icon={PlusIcon}
              onClick={() => setShowAddHats(!showAddHats)}
            >
              Add from order
            </Button>
            <Button
              variant="plain"
              onClick={() => setShowManualHat(!showManualHat)}
            >
              Add custom hat
            </Button>
          </InlineStack>
        )}

        {/* Add Hats from Order Picker */}
        {showAddHats && (
          <Box padding="300" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="200">
              <Text variant="headingSm" as="h4">Available hats from order</Text>
              {availableHats.length === 0 ? (
                <Text tone="subdued" as="p">All hats have been assigned</Text>
              ) : (
                availableHats.map((hat) => (
                  <InlineStack
                    key={hat.shopifyLineItemId}
                    align="space-between"
                    blockAlign="center"
                  >
                    <Text variant="bodyMd" as="span">
                      {hat.productTitle}
                      {hat.color ? ` - ${hat.color}` : ''}
                      {hat.size ? ` (${hat.size})` : ''}
                      {' '}
                      <Text tone="subdued" variant="bodySm" as="span">
                        ({hat.quantity} available)
                      </Text>
                    </Text>
                    <InlineStack gap="100">
                      {hat.quantity > 1 && (
                        <Button
                          size="slim"
                          onClick={() => addHatsFromOrder(hat, 1)}
                        >
                          Add 1
                        </Button>
                      )}
                      <Button
                        size="slim"
                        variant="primary"
                        onClick={() => addHatsFromOrder(hat, hat.quantity)}
                      >
                        Add {hat.quantity > 1 ? `all ${hat.quantity}` : '1'}
                      </Button>
                    </InlineStack>
                  </InlineStack>
                ))
              )}
            </BlockStack>
          </Box>
        )}

        {/* Manual Hat Form */}
        {showManualHat && (
          <Box padding="300" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="200">
              <Text variant="headingSm" as="h4">Add custom hat</Text>
              <InlineStack gap="200">
                <div style={{ flex: 2 }}>
                  <TextField
                    label="Color"
                    value={manualColor}
                    onChange={setManualColor}
                    placeholder="e.g. Navy"
                    autoComplete="off"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Size"
                    value={manualSize}
                    onChange={setManualSize}
                    placeholder="e.g. L"
                    autoComplete="off"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Qty"
                    type="number"
                    value={manualQty}
                    onChange={setManualQty}
                    min={1}
                    autoComplete="off"
                  />
                </div>
              </InlineStack>
              <Button
                onClick={addManualHat}
                disabled={!manualColor.trim()}
              >
                Add Hat
              </Button>
            </BlockStack>
          </Box>
        )}
      </BlockStack>
    </Card>
  );
}

// ============================================
// Order Prep Card Component
// ============================================

function OrderPrepCard({
  order,
  onSent,
}: {
  order: PrepOrder;
  onSent: () => void;
}) {
  const [assignments, setAssignments] = useState<AssignmentDraft[]>(
    order.productionAssignments.length > 0 ? order.productionAssignments : []
  );
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveTimer, setSaveTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [previewImage, setPreviewImage] = useState<{ url: string; alt: string } | null>(null);
  const assignmentsRef = useRef(assignments);
  assignmentsRef.current = assignments;

  const digitizedFiles = order.files.filter((f) => f.type === 'digitization_result');

  // Calculate assignment stats
  const totalOrderHats = order.parsedHats.reduce((sum, h) => sum + h.quantity, 0);
  const assignedQtyByLineItem: Record<string, number> = {};
  for (const a of assignments) {
    for (const hat of a.hatItems) {
      if (hat.sourceLineItemId) {
        assignedQtyByLineItem[hat.sourceLineItemId] =
          (assignedQtyByLineItem[hat.sourceLineItemId] || 0) + hat.quantity;
      }
    }
  }
  const totalAssignedHats = Object.values(assignedQtyByLineItem).reduce((a, b) => a + b, 0) +
    assignments.reduce(
      (sum, a) => sum + a.hatItems.filter((h) => h.isManualOverride).reduce((s, h) => s + h.quantity, 0),
      0
    );
  const allAssigned = order.parsedHats.every(
    (hat) => (assignedQtyByLineItem[hat.shopifyLineItemId] || 0) >= hat.quantity
  );
  const hasAssignments = assignments.length > 0;
  const allHaveHats = assignments.every((a) => a.hatItems.length > 0);
  const allHaveMockup = assignments.every((a) => a.mockupFileId);
  const allHaveType = order.productType !== 'both' || assignments.every((a) => a.productType);
  const canSend = hasAssignments && allHaveHats && allHaveMockup && allHaveType;

  // Compute available hats for each assignment (what's left unassigned)
  const getAvailableHats = (excludeAssignmentIndex: number): ParsedHat[] => {
    const usedByOthers: Record<string, number> = {};
    assignments.forEach((a, idx) => {
      if (idx === excludeAssignmentIndex) return;
      for (const hat of a.hatItems) {
        if (hat.sourceLineItemId) {
          usedByOthers[hat.sourceLineItemId] =
            (usedByOthers[hat.sourceLineItemId] || 0) + hat.quantity;
        }
      }
    });
    // Also subtract what's already in the current assignment
    const usedByCurrent: Record<string, number> = {};
    for (const hat of assignments[excludeAssignmentIndex]?.hatItems || []) {
      if (hat.sourceLineItemId) {
        usedByCurrent[hat.sourceLineItemId] =
          (usedByCurrent[hat.sourceLineItemId] || 0) + hat.quantity;
      }
    }

    return order.parsedHats
      .map((hat) => {
        const used = (usedByOthers[hat.shopifyLineItemId] || 0) + (usedByCurrent[hat.shopifyLineItemId] || 0);
        const remaining = hat.quantity - used;
        return remaining > 0 ? { ...hat, quantity: remaining } : null;
      })
      .filter((h): h is ParsedHat => h !== null);
  };

  // Debounced auto-save
  const debouncedSave = useCallback(
    (newAssignments: AssignmentDraft[]) => {
      if (saveTimer) clearTimeout(saveTimer);
      const timer = setTimeout(async () => {
        // Only save assignments that have at least a mockup selected
        const toSave = newAssignments.filter((a) => a.mockupFileId);
        if (toSave.length === 0) return;
        setSaving(true);
        try {
          await saveAssignments(order.id, toSave);
        } catch (err) {
          console.error('Auto-save failed:', err);
        } finally {
          setSaving(false);
        }
      }, 1500);
      setSaveTimer(timer);
    },
    [order.id, saveTimer]
  );

  const updateAssignment = useCallback(
    (index: number, updated: AssignmentDraft) => {
      setAssignments((prev) => {
        const newArr = [...prev];
        newArr[index] = updated;
        debouncedSave(newArr);
        return newArr;
      });
      setError(null);
    },
    [debouncedSave]
  );

  const addAssignment = useCallback(() => {
    const mockups = order.files.filter((f) => f.type === 'proof' || f.type === 'mockup');
    setAssignments((prev) => [
      ...prev,
      {
        mockupFileId: mockups.length === 1 ? mockups[0].id : '',
        hatItems: [],
      },
    ]);
    setError(null);
  }, [order.files]);

  const deleteAssignment = useCallback(
    (index: number) => {
      setAssignments((prev) => {
        const newArr = prev.filter((_, i) => i !== index);
        debouncedSave(newArr);
        return newArr;
      });
      setError(null);
    },
    [debouncedSave]
  );

  const handleSendToProduction = useCallback(async () => {
    // Final save before sending
    const toSave = assignmentsRef.current.filter((a) => a.mockupFileId);
    if (toSave.length === 0) {
      setError('Add at least one assignment before sending to production');
      return;
    }

    setSending(true);
    setError(null);
    try {
      await saveAssignments(order.id, toSave);
      await sendOrderToProduction(order.id, !allAssigned);
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send to production');
    } finally {
      setSending(false);
    }
  }, [order.id, allAssigned, onSent]);

  const handleDownload = useCallback(
    (fileId: string) => {
      const url = buildDownloadUrl(fileId, order.customerName, order.shopifyOrderNumber);
      window.open(url, '_blank');
    },
    [order.customerName, order.shopifyOrderNumber]
  );

  const progressPercent = totalOrderHats > 0 ? Math.min(100, Math.round((totalAssignedHats / totalOrderHats) * 100)) : 0;

  return (
    <Card key={order.id}>
      <BlockStack gap="400">
        {/* Header */}
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="300" blockAlign="center">
            <Text variant="headingLg" as="h2">
              {order.shopifyOrderName}
            </Text>
            <Badge tone={order.productType === 'embroidery' ? 'success' : order.productType === 'both' ? 'info' : 'warning'}>
              {order.productType === 'both' ? 'embroidery & patch' : order.productType}
            </Badge>
            {saving && <Spinner size="small" />}
          </InlineStack>
          <Text variant="bodySm" tone="subdued" as="span">
            {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''}
          </Text>
        </InlineStack>

        {/* Customer */}
        <Text variant="bodyMd" as="p">
          <Text fontWeight="semibold" as="span">Customer:</Text> {order.customerName}
        </Text>

        {/* Order Notes */}
        {order.shopifyNote && (
          <Box background="bg-surface-secondary" padding="300" borderRadius="200">
            <Text variant="bodySm" as="p">
              <Text fontWeight="semibold" as="span">Order Notes:</Text> {order.shopifyNote}
            </Text>
          </Box>
        )}

        <Divider />

        {/* Section 1: Hat Inventory */}
        <Text variant="headingMd" as="h3">Hat Inventory</Text>
        <BlockStack gap="200">
          {order.parsedHats.map((hat) => {
            const assigned = assignedQtyByLineItem[hat.shopifyLineItemId] || 0;
            const fullyAssigned = assigned >= hat.quantity;
            return (
              <InlineStack
                key={hat.shopifyLineItemId}
                align="space-between"
                blockAlign="center"
              >
                <InlineStack gap="200" blockAlign="center">
                  {fullyAssigned && <Icon source={CheckIcon} tone="success" />}
                  <Text variant="bodyMd" as="span">
                    {hat.productTitle}
                    {hat.color ? ` - ${hat.color}` : ''}
                    {hat.size ? ` (${hat.size})` : ''}
                  </Text>
                </InlineStack>
                <InlineStack gap="200" blockAlign="center">
                  <Text
                    variant="bodyMd"
                    as="span"
                    tone={fullyAssigned ? 'success' : 'subdued'}
                    fontWeight="semibold"
                  >
                    {assigned}/{hat.quantity}
                  </Text>
                  {fullyAssigned ? (
                    <Badge tone="success">Assigned</Badge>
                  ) : assigned > 0 ? (
                    <Badge tone="attention">Partial</Badge>
                  ) : (
                    <Badge>Unassigned</Badge>
                  )}
                </InlineStack>
              </InlineStack>
            );
          })}
        </BlockStack>

        <Divider />

        {/* Section 2: Assignments Builder */}
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd" as="h3">Assignments</Text>
          <Button icon={PlusIcon} onClick={addAssignment}>
            Add Assignment
          </Button>
        </InlineStack>

        {assignments.length === 0 ? (
          <Banner tone="info">
            <p>
              Create assignments to map mockups to hats. Each assignment groups a mockup with the
              specific hats it applies to.
            </p>
          </Banner>
        ) : (
          <BlockStack gap="400">
            {assignments.map((assignment, idx) => (
              <AssignmentCard
                key={assignment.id || idx}
                assignment={assignment}
                index={idx}
                order={order}
                onUpdate={(updated) => updateAssignment(idx, updated)}
                onDelete={() => deleteAssignment(idx)}
                availableHats={getAvailableHats(idx)}
                onImageClick={(url, alt) => setPreviewImage({ url, alt })}
              />
            ))}
          </BlockStack>
        )}

        {/* Digitized Files Download */}
        {digitizedFiles.length > 0 && (
          <>
            <Divider />
            <Text variant="headingSm" as="h3">Digitized Files</Text>
            <BlockStack gap="200">
              {digitizedFiles.map((file) => (
                <InlineStack key={file.id} gap="300" blockAlign="center">
                  <Text variant="bodyMd" as="span">{file.originalFilename}</Text>
                  <Button
                    size="slim"
                    icon={ExportIcon}
                    onClick={() => handleDownload(file.id)}
                  >
                    Download: {order.customerName.toLowerCase()} - {order.shopifyOrderNumber}
                  </Button>
                </InlineStack>
              ))}
            </BlockStack>
          </>
        )}

        <Divider />

        {/* Section 3: Actions */}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="bodyMd" as="span">
              {totalAssignedHats} of {totalOrderHats} hats assigned
            </Text>
            {!allAssigned && hasAssignments && (
              <Badge tone="attention">Not all hats assigned</Badge>
            )}
          </InlineStack>
          <ProgressBar progress={progressPercent} tone="primary" size="small" />
        </BlockStack>

        <InlineStack align="end" gap="200">
          <Button
            variant="primary"
            size="large"
            disabled={!canSend || sending}
            loading={sending}
            onClick={handleSendToProduction}
          >
            {!hasAssignments
              ? 'Add assignments first'
              : !allHaveMockup
              ? 'Select mockups first'
              : !allHaveHats
              ? 'Assign hats first'
              : !allHaveType
              ? 'Set embellishment type on each assignment'
              : allAssigned
              ? 'Send to Production'
              : 'Send to Production (partial)'}
          </Button>
        </InlineStack>
      </BlockStack>

      {/* Image Preview Modal */}
      <Modal
        open={previewImage !== null}
        onClose={() => setPreviewImage(null)}
        title={previewImage?.alt || 'Image Preview'}
        size="large"
      >
        <Modal.Section>
          {previewImage && (
            <div style={{ textAlign: 'center' }}>
              <img
                src={previewImage.url}
                alt={previewImage.alt}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            </div>
          )}
        </Modal.Section>
      </Modal>
    </Card>
  );
}

// ============================================
// Main Page
// ============================================

export default function ProductionPrep() {
  const [orders, setOrders] = useState<PrepOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getProductionPrepOrders();
      setOrders(response.orders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleOrderSent = useCallback((orderId: string) => {
    setOrders((prev) => prev.filter((o) => o.id !== orderId));
  }, []);

  return (
    <Page
      title="Production Prep Queue"
      subtitle="Review approved orders, assign mockups to hats, then send to production"
      primaryAction={{
        content: 'Refresh',
        icon: RefreshIcon,
        onAction: fetchOrders,
        loading: loading,
      }}
    >
      <Layout>
        <Layout.Section>
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}

          {loading && orders.length === 0 ? (
            <Card>
              <Box padding="600">
                <InlineStack align="center" blockAlign="center">
                  <Spinner size="large" />
                </InlineStack>
              </Box>
            </Card>
          ) : orders.length === 0 ? (
            <Card>
              <Box padding="600">
                <BlockStack gap="200" inlineAlign="center">
                  <Text variant="headingMd" as="p">No orders ready for production prep</Text>
                  <Text tone="subdued" as="p">
                    Orders will appear here after they've been approved by customers.
                  </Text>
                </BlockStack>
              </Box>
            </Card>
          ) : (
            <BlockStack gap="600">
              <Text variant="bodyMd" tone="subdued" as="p">
                {orders.length} order{orders.length !== 1 ? 's' : ''} ready for review
              </Text>
              {orders.map((order) => (
                <OrderPrepCard
                  key={order.id}
                  order={order}
                  onSent={() => handleOrderSent(order.id)}
                />
              ))}
            </BlockStack>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}
